<script setup lang="ts">
/**
 * Inline preview of a file by its kind: images (fit to the box; a click
 * toggles actual size), PDFs in the browser's viewer, text, JSON, CSV and
 * code as a code block (fetched, up to `maxTextBytes`), audio and video with
 * the native controls (a video shows `poster` until it plays). Anything
 * else — or a file too large to show as text — gets its type icon, name,
 * size and a download link.
 *
 * `src` must be a URL the browser may load (object URL, same-origin or CORS
 * enabled). Loading and error states are built in; `error` also covers a
 * failed text fetch or a broken image.
 */
import Download from 'lucide-vue-next/dist/esm/icons/download.js'; import LoaderCircle from 'lucide-vue-next/dist/esm/icons/loader-circle.js';
import { computed, inject, onBeforeUnmount, ref, watch } from 'vue';
import { formatBytes, fileExtension, fileKind } from '../Attachment/fileKind';
import NxFileTypeIcon from '../Attachment/NxFileTypeIcon.vue';
import NxCodeBlock from '../CodeBlock/NxCodeBlock.vue';
import { i18nInjectionKey } from '../internal';

const props = withDefaults(
  defineProps<{
    src: string;
    name: string;
    /** MIME type; the name's extension is used when it is missing or generic. */
    type?: string;
    size?: number | null;
    /** Video only: the picture shown before it plays (e.g. its thumbnail). */
    poster?: string;
    /** Largest text file shown inline, in bytes. */
    maxTextBytes?: number;
    downloadLabel?: string;
    noPreviewLabel?: string;
    tooLargeLabel?: string;
    errorLabel?: string;
    loadingLabel?: string;
    locale?: string;
  }>(),
  {
    type: undefined,
    size: null,
    poster: undefined,
    maxTextBytes: 1024 * 1024,
    downloadLabel: 'Herunterladen',
    noPreviewLabel: 'Für diesen Dateityp gibt es keine Vorschau.',
    tooLargeLabel: 'Zu groß für die Vorschau.',
    errorLabel: 'Die Datei konnte nicht geladen werden.',
    loadingLabel: 'Wird geladen …',
    locale: undefined,
  }
);

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value);

const TEXT_EXTENSIONS = new Set([
  'csv',
  'tsv',
  'md',
  'log',
  'txt',
  'xml',
  'yml',
  'yaml',
  'ini',
  'toml',
]);

type Mode = 'image' | 'pdf' | 'text' | 'audio' | 'video' | 'none';

const kind = computed(() => fileKind(props.name, props.type));
const mode = computed<Mode>(() => {
  const k = kind.value;
  if (k === 'image') return 'image';
  if (k === 'pdf') return 'pdf';
  if (k === 'audio') return 'audio';
  if (k === 'video') return 'video';
  if (k === 'text' || k === 'code' || TEXT_EXTENSIONS.has(fileExtension(props.name))) return 'text';
  return 'none';
});
const tooLarge = computed(
  () => mode.value === 'text' && typeof props.size === 'number' && props.size > props.maxTextBytes
);
const language = computed(() => fileExtension(props.name).toUpperCase() || undefined);

const state = ref<'loading' | 'ready' | 'error'>('loading');
const text = ref('');
const zoomed = ref(false);
let aborter: AbortController | null = null;

/**
 * The body as text, reading at most `maxBytes` and cancelling the rest — a
 * wrong or missing `size` must not pull a huge file into memory. Falls back
 * to `text()` where the body cannot be streamed.
 */
async function readCapped(res: Response, maxBytes: number): Promise<string> {
  const reader = res.body?.getReader?.();
  if (!reader) {
    const whole = await res.text();
    return new TextDecoder().decode(new TextEncoder().encode(whole).slice(0, maxBytes));
  }
  const decoder = new TextDecoder();
  let out = '';
  let read = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    const room = maxBytes - read;
    const chunk = value.byteLength > room ? value.subarray(0, room) : value;
    read += chunk.byteLength;
    out += decoder.decode(chunk, { stream: true });
    if (read >= maxBytes) {
      void reader.cancel();
      break;
    }
  }
  return out + decoder.decode();
}

async function loadText() {
  aborter?.abort();
  const own = typeof AbortController !== 'undefined' ? new AbortController() : null;
  aborter = own;
  state.value = 'loading';
  try {
    const res = await fetch(props.src, { signal: own?.signal });
    if (!res.ok) throw new Error(String(res.status));
    const body = await readCapped(res, props.maxTextBytes);
    // A response that arrives after the file changed must not replace the newer one.
    if (own?.signal.aborted) return;
    text.value = body;
    state.value = 'ready';
  } catch (e) {
    if ((e as Error)?.name === 'AbortError' || own?.signal.aborted) return;
    state.value = 'error';
  }
}

watch(
  () => [props.src, mode.value, tooLarge.value] as const,
  () => {
    zoomed.value = false;
    text.value = '';
    if (mode.value === 'text' && !tooLarge.value) {
      void loadText();
    } else {
      aborter?.abort();
      // Media report their own load; the fallback has nothing to load.
      state.value = mode.value === 'none' || tooLarge.value ? 'ready' : 'loading';
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => aborter?.abort());

const isJson = computed(
  () => fileExtension(props.name) === 'json' || props.type === 'application/json'
);
const jsonValue = computed(() => {
  if (!isJson.value) return undefined;
  try {
    return JSON.parse(text.value) as unknown;
  } catch {
    return undefined;
  }
});
const sizeText = computed(() =>
  typeof props.size === 'number' ? formatBytes(props.size, locale.value) : ''
);
</script>

<template>
  <div class="nx-file-preview" :class="`mode-${mode}`" :data-state="state">
    <div
      v-if="state === 'loading' && mode !== 'none'"
      class="nx-file-preview__loading"
      role="status"
    >
      <LoaderCircle class="nx-file-preview__spinner nxt-icon-lg" aria-hidden="true" />
      <span>{{ props.loadingLabel }}</span>
    </div>

    <div v-if="state === 'error'" class="nx-file-preview__fallback" role="alert">
      <NxFileTypeIcon :name="props.name" :type="props.type" size="lg" />
      <strong class="nx-file-preview__name">{{ props.name }}</strong>
      <span class="nx-file-preview__note">{{ props.errorLabel }}</span>
      <a class="nx-file-preview__download" :href="props.src" :download="props.name">
        <Download class="nxt-icon-sm" aria-hidden="true" />
        {{ props.downloadLabel }}
      </a>
    </div>

    <template v-else-if="mode === 'image'">
      <button
        type="button"
        class="nx-file-preview__image-wrap"
        :class="{ 'is-zoomed': zoomed }"
        :aria-pressed="zoomed"
        :aria-label="props.name"
        @click="zoomed = !zoomed"
      >
        <img
          class="nx-file-preview__image"
          :src="props.src"
          :alt="props.name"
          data-testid="nx-file-preview-image"
          @load="state = 'ready'"
          @error="state = 'error'"
        />
      </button>
    </template>

    <iframe
      v-else-if="mode === 'pdf'"
      class="nx-file-preview__frame"
      :src="props.src"
      :title="props.name"
      data-testid="nx-file-preview-pdf"
      @load="state = 'ready'"
    />

    <audio
      v-else-if="mode === 'audio'"
      class="nx-file-preview__audio"
      :src="props.src"
      controls
      preload="metadata"
      @loadedmetadata="state = 'ready'"
      @error="state = 'error'"
    />

    <video
      v-else-if="mode === 'video'"
      class="nx-file-preview__video"
      :src="props.src"
      :poster="props.poster"
      controls
      preload="metadata"
      @loadedmetadata="state = 'ready'"
      @error="state = 'error'"
    />

    <template v-else-if="mode === 'text' && !tooLarge">
      <NxCodeBlock
        v-if="state === 'ready'"
        class="nx-file-preview__text"
        :json="jsonValue"
        :code="jsonValue === undefined ? text : undefined"
        :language="language"
        line-numbers
        data-testid="nx-file-preview-text"
      />
    </template>

    <div v-else class="nx-file-preview__fallback" data-testid="nx-file-preview-fallback">
      <NxFileTypeIcon :name="props.name" :type="props.type" size="lg" />
      <strong class="nx-file-preview__name">{{ props.name }}</strong>
      <span v-if="sizeText" class="nx-file-preview__size">{{ sizeText }}</span>
      <span class="nx-file-preview__note">
        {{ tooLarge ? props.tooLargeLabel : props.noPreviewLabel }}
      </span>
      <a class="nx-file-preview__download" :href="props.src" :download="props.name">
        <Download class="nxt-icon-sm" aria-hidden="true" />
        {{ props.downloadLabel }}
      </a>
    </div>
  </div>
</template>

<style scoped>
.nx-file-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: var(--nxt-area-size-md);
  overflow: hidden;
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
}
.nx-file-preview.mode-image {
  border-radius: 0;
  background: transparent;
}
.nx-file-preview__loading {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  background: var(--nxt-surface-sunken-bg);
}
.nx-file-preview__spinner {
  animation: nx-file-preview-spin 0.9s linear infinite;
}
.nx-file-preview__image-wrap {
  display: grid;
  flex: 1 1 auto;
  place-items: center;
  min-height: 0;
  padding: 0;
  overflow: auto;
  border: none;
  background: transparent;
  cursor: zoom-in;
}
.nx-file-preview__image-wrap.is-zoomed {
  place-items: start;
  cursor: zoom-out;
}
.nx-file-preview__image-wrap:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-file-preview__image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.nx-file-preview__image-wrap.is-zoomed .nx-file-preview__image {
  max-width: none;
  max-height: none;
}
.nx-file-preview__frame {
  flex: 1 1 auto;
  width: 100%;
  min-height: var(--nxt-area-size-lg);
  border: none;
  background: var(--nxt-surface-bg);
}
.nx-file-preview__audio {
  margin: auto;
  width: min(100%, var(--nxt-panel-width-2xl));
}
.nx-file-preview__video {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  background: black;
}
.nx-file-preview__text {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}
.nx-file-preview__fallback {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-lg);
  text-align: center;
}
.nx-file-preview__name {
  max-width: 100%;
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-file-preview__size,
.nx-file-preview__note {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
.nx-file-preview__download {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin-block-start: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-raised-bg);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  text-decoration: none;
}
.nx-file-preview__download:hover {
  border-color: var(--nxt-border-strong);
}
.nx-file-preview__download:focus-visible {
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
@keyframes nx-file-preview-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-file-preview__spinner {
    animation: none;
  }
}
</style>
